/**
 * Connectors feature (Preact): lists the workspace's configured connector
 * instances and lets the user create, edit, enable/disable, and delete them.
 * Each connector is an instance of a registered connector plugin kind; its
 * settings are a JSON config edited graphically via a generic form that is
 * driven by the kind's JSON config schema and defaults.
 */
import { useState, useEffect, useCallback } from 'preact/hooks';
import {
  listConnectorTypes,
  listConnectors,
  createConnector,
  updateConnector,
  deleteConnector,
} from '../lib/api.js';
import { Icon } from '../components/Icons.jsx';

export function ConnectorPage() {
  const [types, setTypes] = useState(null); // null | TypeInfo[]
  const [connectors, setConnectors] = useState(null); // null | Connector[]
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null); // null | 'new' | Connector
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [ts, cs] = await Promise.all([listConnectorTypes(), listConnectors()]);
      setTypes(ts);
      setConnectors(cs);
      setError('');
    } catch (e) {
      setError(e.message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (c) => {
    try {
      await updateConnector(c.id, { name: c.name, enabled: !c.enabled, config: c.config });
      load();
    } catch (e) {
      window.alert(`Toggle failed: ${e.message}`);
    }
  };

  const onDelete = async (c) => {
    if (!window.confirm(`Delete connector "${c.name}"?`)) return;
    try {
      await deleteConnector(c.id);
      load();
    } catch (e) {
      window.alert(`Delete failed: ${e.message}`);
    }
  };

  if (editing) {
    return (
      <ConnectorForm
        types={types || []}
        connector={editing === 'new' ? null : editing}
        onCancel={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          load();
        }}
        onSavingChange={setSaving}
      />
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="flex items-center gap-4 px-4 py-3 border-b border-base-300 bg-base-100">
        <h1 className="text-lg font-semibold text-base-content truncate">Connectors</h1>
        <button
          type="button"
          className="btn btn-primary btn-sm ml-auto"
          disabled={saving}
          onClick={() => setEditing('new')}
        >
          <Icon name="plus" className="w-4 h-4" />
          New Connector
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4">
        <div className="w-full max-w-3xl mx-auto flex flex-col gap-3">
          {error ? (
            <p className="text-base-content/60 text-sm">Failed to load connectors.</p>
          ) : connectors === null ? (
            <p className="text-base-content/60 text-sm">Loading…</p>
          ) : connectors.length === 0 ? (
            <p className="text-base-content/50 text-sm text-center py-8">
              No connectors yet. Create one to connect the agent to an external service.
            </p>
          ) : (
            connectors.map((c) => (
              <ConnectorRow
                key={c.id}
                connector={c}
                onEdit={() => setEditing(c)}
                onToggle={() => toggle(c)}
                onDelete={() => onDelete(c)}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function ConnectorRow({ connector: c, onEdit, onToggle, onDelete }) {
  return (
    <div className="rounded-xl border border-base-300 bg-base-100 p-4">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`badge badge-sm ${c.enabled ? 'badge-success' : 'badge-ghost'}`}>
              {c.enabled ? 'enabled' : 'disabled'}
            </span>
            <span className="text-xs text-base-content/50">{c.kind}</span>
          </div>
          <p className="mt-1 text-sm font-medium text-base-content">{c.name}</p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button type="button" className="btn btn-ghost btn-xs" onClick={onToggle}>
            {c.enabled ? 'Disable' : 'Enable'}
          </button>
          <button type="button" className="btn btn-ghost btn-xs" onClick={onEdit}>
            Edit
          </button>
          <button type="button" className="btn btn-ghost btn-xs text-error" onClick={onDelete}>
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

// ConnectorForm renders the create/edit editor. When creating, the user first
// picks a kind, then edits the name and the kind's JSON config via the generic
// schema-driven editor.
function ConnectorForm({ types, connector, onCancel, onSaved, onSavingChange }) {
  const [kind, setKind] = useState(connector ? connector.kind : '');
  const [name, setName] = useState(connector ? connector.name : '');
  const [enabled, setEnabled] = useState(connector ? connector.enabled : true);
  const [config, setConfig] = useState(null); // current config object
  const [err, setErr] = useState('');

  const type = types.find((t) => t.kind === kind) || null;

  // Initialise config from the stored value (for edit) or the kind's defaults
  // (for create) whenever the kind changes.
  useEffect(() => {
    if (connector) {
      const parsed = typeof connector.config === 'string' ? JSON.parse(connector.config) : connector.config;
      setConfig(parsed || {});
    } else if (type) {
      setConfig({ ...(type.default_config || {}) });
    }
  }, [type, connector]);

  const submit = async (e) => {
    e.preventDefault();
    if (!type) {
      setErr('Please choose a connector type.');
      return;
    }
    if (!name.trim()) {
      setErr('Name is required.');
      return;
    }
    setErr('');
    const payload = { name: name.trim(), config: config || {} };
    onSavingChange(true);
    try {
      if (connector) await updateConnector(connector.id, { ...payload, enabled });
      else await createConnector({ kind, ...payload });
      onSaved();
    } catch (ex) {
      setErr(ex.message);
    } finally {
      onSavingChange(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="flex items-center gap-4 px-4 py-3 border-b border-base-300 bg-base-100">
        <h1 className="text-lg font-semibold text-base-content truncate">
          {connector ? 'Edit Connector' : 'New Connector'}
        </h1>
        <button type="button" className="btn btn-ghost btn-sm ml-auto" onClick={onCancel}>
          Cancel
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4">
        <form onSubmit={submit} className="w-full max-w-xl mx-auto flex flex-col gap-3">
          {!connector && (
            <label className="flex flex-col gap-1 text-sm text-base-content/80">
              Type
              <select
                className="select select-bordered select-sm"
                value={kind}
                onChange={(e) => setKind(e.target.value)}
              >
                <option value="">Select a type…</option>
                {types.map((t) => (
                  <option key={t.kind} value={t.kind}>
                    {t.label || t.kind}
                  </option>
                ))}
              </select>
              {type && type.description && (
                <span className="text-xs text-base-content/50">{type.description}</span>
              )}
            </label>
          )}

          {type && (
            <>
              <label className="flex flex-col gap-1 text-sm text-base-content/80">
                Name
                <input
                  type="text"
                  className="input input-bordered input-sm"
                  placeholder="e.g. My calendar"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </label>

              <ConnectorConfig
                schema={type.config_schema}
                value={config}
                onChange={setConfig}
                connectorId={connector ? connector.id : null}
              />
            </>
          )}

          {connector && (
            <label className="flex items-center gap-2 text-sm text-base-content/80">
              <input
                type="checkbox"
                className="toggle toggle-sm"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
              />
              Enabled
            </label>
          )}

          {err && <p className="text-error text-sm">{err}</p>}

          <div className="flex items-center gap-2 pt-2">
            <button type="submit" className="btn btn-primary btn-sm">
              Save
            </button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
// ConnectorConfig renders a generic, schema-driven editor for a connector's
// JSON config. It builds a field per property in the JSON Schema, rendering
// text, textarea, number, boolean and select inputs (and password/secret
// fields per "format": "password"). Fields with an "x_if" condition (e.g.
// "auth_type=basic") are only shown when the condition holds against the
// current config. When the connector instance is an OAuth connector, an
// "Authorize" action is rendered so the user can start the OAuth flow. It lifts
// the resulting config object up via onChange.
function ConnectorConfig({ schema, value, onChange, connectorId }) {
  if (!schema || schema.type !== 'object' || !schema.properties) {
    return null;
  }
  const obj = value && typeof value === 'object' ? value : {};
  const required = new Set(schema.required || []);

  const setField = (key, next) => {
    const out = { ...obj, [key]: next };
    onChange(out);
  };

  // evalIf parses a simple "x_if" condition of the form "field=value" against
  // the current config.
  const evalIf = (cond) => {
    if (!cond) return true;
    const m = String(cond).match(/^([^=]+)=(.*)$/);
    if (!m) return true;
    return String(obj[m[1]] ?? '') === m[2];
  };

  const props = Object.entries(schema.properties || {}).filter(([, prop]) => evalIf(prop.x_if));
  const isOAuth = obj.provider === 'google';

  return (
    <div className="flex flex-col gap-3">
      {isOAuth && (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-base-300 bg-base-100 p-3">
          <div className="text-sm text-base-content">
            <div className="font-medium">OAuth authorization</div>
            <div className="text-xs text-base-content/50">
              Connect this connector to your account. Tokens are stored and refreshed automatically.
            </div>
          </div>
          {connectorId ? (
            <a className="btn btn-primary btn-sm" href={`/api/connectors/${connectorId}/authorize`}>
              Authorize
            </a>
          ) : (
            <span className="text-xs text-base-content/50">Save the connector to authorize it.</span>
          )}
        </div>
      )}

      {props.length === 0 && (
        <p className="text-xs text-base-content/50">This connector has no configuration options.</p>
      )}
      {props.map(([key, prop]) => (
        <ConfigField
          key={key}
          name={key}
          prop={prop}
          value={obj[key] ?? defaultValue(prop)}
          required={required.has(key)}
          onChange={(v) => setField(key, v)}
        />
      ))}
    </div>
  );
}

// defaultValue returns the schema's default, or a type-appropriate empty value.
function defaultValue(prop) {
  if (Object.prototype.hasOwnProperty.call(prop, 'default')) return prop.default;
  switch (prop.type) {
    case 'boolean':
      return false;
    case 'integer':
    case 'number':
      return '';
    case 'array':
      return [];
    case 'object':
      return {};
    default:
      return '';
  }
}

function ConfigField({ name, prop, value, required, onChange }) {
  const label = prop.title || name;
  const type = prop.type;
  const isSecret = prop.format === 'password';
  const isMultiline = prop.format === 'multiline' || type === 'textarea';

  let control;
  if (type === 'boolean') {
    control = (
      <input
        type="checkbox"
        className="toggle toggle-sm"
        checked={!!value}
        onChange={(e) => onChange(e.target.checked)}
      />
    );
  } else if (Array.isArray(prop.enum)) {
    const labels = prop.x_labels || {};
    control = (
      <select
        className="select select-bordered select-sm"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
      >
        {prop.enum.map((opt) => (
          <option key={String(opt)} value={opt}>
            {labels[opt] || String(opt)}
          </option>
        ))}
      </select>
    );
  } else if (type === 'integer' || type === 'number') {
    control = (
      <input
        type="number"
        className="input input-bordered input-sm"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
      />
    );
  } else if (isMultiline) {
    control = (
      <textarea
        className="textarea textarea-bordered textarea-sm font-mono"
        rows={4}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  } else if (isSecret) {
    // Secret fields are blanked unless the user types, so stored values are
    // not echoed back into the DOM.
    control = (
      <input
        type="password"
        className="input input-bordered input-sm"
        placeholder={value ? '•••••••• (unchanged)' : 'Enter value'}
        defaultValue=""
        onChange={(e) => onChange(e.target.value)}
      />
    );
  } else {
    control = (
      <input
        type="text"
        className="input input-bordered input-sm"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  return (
    <label className="flex flex-col gap-1 text-sm text-base-content/80">
      <span>
        {label}
        {required && <span className="text-error ml-1">*</span>}
      </span>
      {control}
      {prop.description && <span className="text-xs text-base-content/50">{prop.description}</span>}
    </label>
  );
}